<template>
  <div class="page">

    <!-- HEADER -->
    <div class="page-header">
      <div>
        <h2>Courses</h2>
        <p>Manage Creator Dost courses.</p>
      </div>

      <button class="btn-primary" @click="openForm()">
        + Add Course
      </button>
    </div>


    <!-- TOOLBAR -->
    <div class="toolbar">

      <input v-model="search" placeholder="Search course..." />

      <select v-model="filterType" class="filter-select">
        <option value="">All Types</option>
        <option value="course">Course</option>
        <option value="workshop">Workshop</option>
        <option value="program">Program</option>
      </select>

    </div>


    <!-- TABLE -->
    <div class="card table-card">

      <div class="table-wrapper">

        <table class="modern-table">

          <thead>
            <tr>
              <th>Course</th>
              <th>Category</th>
              <th>Creator</th>
              <th>Type</th>
              <th>Price</th>
              <th>Duration</th>
              <th>Language</th>
              <th style="text-align:right;">Actions</th>
            </tr>
          </thead>

          <tbody>

            <tr v-for="course in filteredCourses" :key="course.course_id">

              <!-- COURSE -->
              <td>
                <div class="course-info">

                  <img v-if="course.course_image" :src="course.course_image" class="course-thumb" />

                  <div v-else class="course-thumb placeholder">
                    <i class="bx bx-book-open"></i>
                  </div>

                  <div class="course-name">
                    <strong>{{ course.title }}</strong>
                    <small>
                      #{{ course.course_id }}
                    </small>
                  </div>

                </div>
              </td>


              <!-- CATEGORY -->
              <td>
                <span class="category-badge">
                  {{ course.category_name }}
                </span>
              </td>


              <!-- CREATOR -->
              <td>
                {{ course.creator_name }}
              </td>


              <!-- TYPE -->
              <td>
                <span class="type-badge">
                  {{ course.course_typ || "Course" }}
                </span>
              </td>


              <!-- PRICE -->
              <td>

                <div class="price-info">

                  <strong>
                    ₹{{ formatMoney(course.gtotal_price || course.price) }}
                  </strong>

                  <span v-if="course.discount" class="discount">
                    {{ course.discount }}{{ course.discount_type === "percentage" ? "%" : " OFF" }}
                  </span>

                </div>

              </td>


              <!-- DURATION -->
              <td>
                {{ course.course_duration || "-" }}
                {{ course.course_duration ? " hrs" : "" }}
              </td>


              <!-- LANGUAGE -->
              <td>
                {{ course.course_language || "-" }}
              </td>


              <!-- ACTIONS -->
              <td style="text-align:right;">

                <button class="icon-btn view" title="View" @click="viewCourse(course)">
                  <i class="bx bx-show"></i>
                </button>

                <button class="icon-btn edit" title="Edit" @click="openForm(course)">
                  <i class="bx bx-edit"></i>
                </button>

                <button class="icon-btn delete" title="Delete" @click="openDelete(course)">
                  <i class="bx bx-trash"></i>
                </button>

              </td>

            </tr>


            <!-- EMPTY -->
            <tr v-if="filteredCourses.length === 0">

              <td colspan="8" class="empty-state">

                <i class="bx bx-book-open"></i>

                <h4>No courses found</h4>

                <p>
                  Try changing your search or filters.
                </p>

              </td>

            </tr>

          </tbody>

        </table>

      </div>

    </div>


    <!-- PAGINATION -->
    <div class="pagination">

      <button :disabled="page === 0" @click="prevPage">
        <i class="bx bx-chevron-left"></i>
        Prev
      </button>

      <span>
        Page {{ page + 1 }}
      </span>

      <button :disabled="page >= totalPages - 1" @click="nextPage">
        Next
        <i class="bx bx-chevron-right"></i>
      </button>

    </div>


    <!-- ADD / EDIT MODAL -->
    <div v-if="showModal" class="modal-overlay" @click.self="close">

      <div class="modal-box large-modal">

        <div class="modal-header">

          <div>
            <h3>
              {{ form.course_id ? "Edit Course" : "Add Course" }}
            </h3>

            <p>
              {{
              form.course_id
              ? "Update course information."
              : "Create a new course."
              }}
            </p>
          </div>

          <button class="modal-close" @click="close">
            <i class="bx bx-x"></i>
          </button>

        </div>


        <!-- BASIC INFORMATION -->
        <div class="form-section">

          <h4>Basic Information</h4>

          <div class="form-grid">

            <div class="form-group full">

              <label>Course Title *</label>

              <input v-model="form.title" placeholder="Enter course title" />
              <small class="field-hint">
                Course titles must be unique; this title becomes the public course URL.
              </small>

            </div>


            <div class="form-group">

              <label>Category</label>

              <select v-model="form.category">

                <option value="">
                  Select Category
                </option>

                <option v-for="category in categories" :key="category.cat_id" :value="category.cat_id">
                  {{ category.cat_name }}
                </option>

              </select>

            </div>


            <div class="form-group">

              <label>Course Type</label>

              <select v-model="form.course_typ">

                <option value="">
                  Select Type
                </option>

                <option value="Normal">
                  Normal
                </option>

                <option value="Video">
                  Video
                </option>

              </select>

            </div>


            <div class="form-group">

              <label>Creator</label>

              <select v-model="form.creator_id">

                <option value="">
                  Select Creator
                </option>

                <option v-for="creator in creators" :key="creator.creator_id" :value="creator.creator_id">
                  {{ creator.cre_username || creator.cre_buss_name || `Creator #${creator.creator_id}` }}
                </option>

              </select>

            </div>

            <div class="form-group">

              <label>Language</label>

              <select v-model="form.course_language">

                <option value="">
                  Select Language
                </option>

                <option>
                  English
                </option>

                <option>
                  Hindi
                </option>

                <option>
                  Hinglish
                </option>

              </select>

            </div>

          </div>

        </div>


        <!-- COURSE MEDIA -->
        <div class="form-section">

          <h4>Course Media</h4>

          <div class="form-grid">

            <div class="form-group">

              <label>Course Image</label>

              <input v-model.trim="form.course_image" type="url" placeholder="https://example.com/image.jpg" />

              <small v-if="formErrors.course_image" class="field-error">
                {{ formErrors.course_image }}
              </small>

            </div>


            <div class="form-group">

              <label>Course Video</label>

              <input v-model.trim="form.course_video" type="url" placeholder="https://example.com/video" />

              <small v-if="formErrors.course_video" class="field-error">
                {{ formErrors.course_video }}
              </small>

            </div>


            <div class="form-group">

              <label>Course Icon</label>

              <input v-model.trim="form.course_icon" type="url" placeholder="https://example.com/icon.svg" />

              <small v-if="formErrors.course_icon" class="field-error">
                {{ formErrors.course_icon }}
              </small>

            </div>

          </div>

        </div>


        <!-- PRICING -->
        <div class="form-section">

          <h4>Pricing</h4>

          <div class="form-grid">

            <div class="form-group">

              <label>Price</label>

              <input type="number" v-model.number="form.price" placeholder="0" />

            </div>


            <div class="form-group">

              <label>GST (%)</label>

              <input type="number" v-model.number="form.gst" placeholder="0" />

            </div>


            <div class="form-group">

              <label>Discount</label>

              <input type="number" v-model.number="form.discount" placeholder="0" />

            </div>


            <div class="form-group">

              <label>Discount Type</label>

              <select v-model="form.discount_type">

                <option value="">
                  Select
                </option>

                <option value="percentage">
                  Percentage
                </option>

                <option value="flat">
                  Flat Amount
                </option>

              </select>

            </div>


            <div class="form-group">

              <label>Final Amount</label>

              <input type="number" :value="calculatedFinalPrice" step="0.01" readonly />

              <small>Calculated automatically after discount and GST.</small>

            </div>

          </div>

        </div>


        <!-- COURSE DETAILS -->
        <div class="form-section">

          <h4>Course Details</h4>

          <div class="form-grid">

            <div class="form-group">

              <label>Duration (Hours)</label>

              <input type="number" v-model.number="form.course_duration" placeholder="0" />

            </div>


            <div class="form-group">

              <label>Validity</label>

              <input v-model="form.course_velidity" placeholder="12 Months" />

            </div>


            <div class="form-group">

              <label>Certification</label>

              <select v-model="form.course_certification">

                <option value="">
                  Select
                </option>

                <option value="Yes">
                  Yes
                </option>

                <option value="No">
                  No
                </option>

              </select>

            </div>


            <div class="form-group full">

              <label>About Course</label>

              <textarea v-model="form.about" placeholder="Write about the course..."></textarea>

            </div>


            <div class="form-group full">

              <label>Course Details</label>

              <textarea v-model="form.details" placeholder="Write detailed course information..."></textarea>

            </div>

          </div>

        </div>


        <!-- FEATURES -->
        <div class="form-section">

          <h4>Course Features</h4>

          <div class="form-group">

            <textarea v-model="form.course_features" placeholder="Enter course features..."></textarea>

          </div>


          <div class="form-group">

            <label>Included Features</label>

            <textarea v-model="form.course_incl_features" placeholder="Enter included features..."></textarea>

          </div>

        </div>


        <!-- MODAL ACTIONS -->
        <div class="modal-actions">

          <button class="btn-secondary" @click="close">
            Cancel
          </button>

          <button class="btn-primary" @click="save">
            {{ form.course_id ? "Update Course" : "Create Course" }}
          </button>

        </div>

      </div>

    </div>


    <!-- VIEW MODAL -->
    <div v-if="viewModal" class="modal-overlay" @click.self="viewModal = false">

      <div class="modal-box view-modal">

        <div class="modal-header">

          <div>
            <h3>Course Details</h3>
            <p>Course #{{ selectedCourse.course_id }}</p>
          </div>

          <button class="modal-close" @click="viewModal = false">
            <i class="bx bx-x"></i>
          </button>

        </div>


        <div class="course-view">

          <img v-if="selectedCourse.course_image" :src="selectedCourse.course_image" class="view-image" />

          <div class="view-content">

            <h2>
              {{ selectedCourse.title }}
            </h2>

            <p class="view-about">
              {{ selectedCourse.about }}
            </p>


            <div class="detail-grid">

              <div>
                <span>Category</span>
                <strong>
                  {{ selectedCourse.category_name }}
                </strong>
              </div>

              <div>
                <span>Creator</span>
                <strong>
                  {{ selectedCourse.creator_name }}
                </strong>
              </div>

              <div>
                <span>Duration</span>
                <strong>
                  {{ selectedCourse.course_duration }} hrs
                </strong>
              </div>

              <div>
                <span>Language</span>
                <strong>
                  {{ selectedCourse.course_language }}
                </strong>
              </div>

              <div>
                <span>Validity</span>
                <strong>
                  {{ selectedCourse.course_velidity }}
                </strong>
              </div>

              <div>
                <span>Certification</span>
                <strong>
                  {{ selectedCourse.course_certification }}
                </strong>
              </div>

            </div>

            <div v-if="selectedCourse.modules?.length" class="course-curriculum-preview">
              <h4>Curriculum</h4>
              <p v-for="module in selectedCourse.modules" :key="module.module_id">
                {{ module.module_title }} · {{ module.lecture_count || 0 }} lectures
              </p>
            </div>

          </div>

        </div>


        <div class="modal-actions">

          <button class="btn-secondary" @click="viewModal = false">
            Close
          </button>

          <button class="btn-secondary" @click="manageCurriculum">
            <i class="bx bx-layer"></i>
            Manage Curriculum
          </button>

          <button class="btn-secondary" @click="manageFaqs">
            <i class="bx bx-help-circle"></i>
            Manage FAQs
          </button>

          <button class="btn-primary" @click="editFromView">
            <i class="bx bx-edit"></i>
            Edit Course
          </button>

        </div>

      </div>

    </div>


    <!-- DELETE MODAL -->
    <div v-if="deleteModal" class="modal-overlay" @click.self="deleteModal = false">

      <div class="modal-box delete-box">

        <div class="delete-icon">
          <i class="bx bx-trash"></i>
        </div>

        <h3>Delete Course?</h3>

        <p>
          Are you sure you want to delete
          <strong>{{ selectedCourse?.title }}</strong>?
          This action cannot be undone.
        </p>

        <div class="modal-actions">

          <button class="btn-secondary" @click="deleteModal = false">
            Cancel
          </button>

          <button class="btn-danger" @click="confirmDelete">
            Delete
          </button>

        </div>

      </div>

    </div>


    <!-- FEEDBACK DIALOG -->
    <div v-if="feedbackModal" class="modal-overlay" @click.self="closeFeedback">

      <div class="modal-box feedback-box">

        <div class="feedback-icon" :class="`feedback-${feedback.type}`">
          <i :class="feedback.type === 'success' ? 'bx bx-check' : 'bx bx-error-circle'"></i>
        </div>

        <h3>{{ feedback.title }}</h3>

        <p>{{ feedback.message }}</p>

        <div class="modal-actions">
          <button class="btn-primary" @click="closeFeedback">OK</button>
        </div>

      </div>

    </div>

  </div>
</template>


<script>
  import {
    getCoursesApi,
    getCourseStatsApi,
    createCourseApi,
    updateCourseApi,
    deleteCourseApi,
  } from "@/apis/courseapis";

  import { getCategoriesApi } from "@/apis/categoryapis";
  import { getCreatorsApi } from "@/apis/creatorapis";
  export default {
    name: "CourseScreen",

    data() {
      return {
        // ================================
        // LIST / FILTER
        // ================================

        categories: [],
        creators: [],
        search: "",
        filterType: "",

        page: 0,
        perPage: 5,

        courses: [],

        totalCourses: 0,
        totalPages: 1,

        // ================================
        // STATS
        // ================================

        courseCount: 0,
        workshopCount: 0,
        programCount: 0,
        newCoursesThisMonth: 0,

        // ================================
        // MODALS
        // ================================

        showModal: false,
        viewModal: false,
        deleteModal: false,

        selectedCourse: null,

        feedbackModal: false,
        feedback: {
          type: "error",
          title: "Something went wrong",
          message: "Please try again.",
        },

        // ================================
        // FORM
        // ================================

        form: {},
        formErrors: {},

        // ================================
        // LOADING
        // ================================

        loading: false,
        saving: false,
        deleting: false,
      };
    },


    // ========================================
    // COMPUTED
    // ========================================

    computed: {
      /*
       * API already performs:
       * - search
       * - type filtering
       * - pagination
       *
       * So no local filtering/pagination required.
       */

      filteredCourses() {
        return this.courses;
      },

      calculatedFinalPrice() {
        const price = Math.max(0, Number(this.form.price) || 0);
        const gstRate = Math.max(0, Number(this.form.gst) || 0);
        const discount = Math.max(0, Number(this.form.discount) || 0);
        const discountAmount = this.form.discount_type === "percentage"
          ? (price * Math.min(discount, 100)) / 100
          : Math.min(discount, price);
        const taxableAmount = price - discountAmount;
        const total = taxableAmount * (1 + gstRate / 100);

        return Math.round((total + Number.EPSILON) * 100) / 100;
      },
    },


    // ========================================
    // WATCH
    // ========================================

    watch: {
      search() {
        this.page = 0;
        this.loadCourses();
      },

      filterType() {
        this.page = 0;
        this.loadCourses();
      },
    },


    // ========================================
    // MOUNTED
    // ========================================

    mounted() {
      this.loadCourses();
      this.loadStats();
      this.loadCategories();
      this.loadCreators();
    },

    // ========================================
    // METHODS
    // ========================================

    methods: {
      // ========================================
      // FORMAT MONEY
      // ========================================
      async loadCategories() {
        try {
          const response = await getCategoriesApi({
            page: 1,
            limit: 1000,
            search: "",
            cat_type: "",
          });

          if (response?.status) {
            this.categories = response.categories || [];
          } else {
            this.categories = [];
          }
        } catch (error) {
          console.error(
            "Load Categories Error:",
            error
          );

          this.categories = [];
        }
      },
      async loadCreators() {
        try {
          const response = await getCreatorsApi({
            page: 1,
            limit: 1000,
            search: "",
            designation: "",
            business: "",
          });

          if (response?.status) {
            this.creators = response.creators || [];
          } else {
            this.creators = [];
          }
        } catch (error) {
          console.error(
            "Load Creators Error:",
            error
          );

          this.creators = [];
        }
      },
      formatMoney(value) {
        return new Intl.NumberFormat("en-IN", {
          maximumFractionDigits: 0,
        }).format(value || 0);
      },


      // ========================================
      // LOAD COURSES
      // ========================================

      async loadCourses() {
        this.loading = true;

        try {
          const response = await getCoursesApi({
            page: this.page + 1,
            limit: this.perPage,
            search: this.search,
            course_typ: this.filterType,
            category: "",
            creator_id: "",
          });

          if (response?.status) {
            this.courses = response.courses || [];

            this.totalCourses = Number(
              response.total || 0
            );

            this.totalPages = Math.max(
              Number(response.totalPages || 1),
              1
            );

            /*
             * API page is 1-based.
             * Vue page is 0-based.
             */

            this.page =
              Math.max(
                Number(response.page || 1) - 1,
                0
              );
          } else {
            this.courses = [];
            this.totalCourses = 0;
            this.totalPages = 1;

            if (response?.success) {
              console.error(response.success);
            }
          }
        } catch (error) {
          console.error(
            "Load Courses Error:",
            error
          );

          this.courses = [];
          this.totalCourses = 0;
          this.totalPages = 1;
        } finally {
          this.loading = false;
        }
      },


      // ========================================
      // LOAD STATS
      // ========================================

      async loadStats() {
        try {
          const response =
            await getCourseStatsApi();

          if (response?.status) {
            this.totalCourses =
              Number(
                response.totalCourses || 0
              );

            this.courseCount =
              Number(
                response.courses || 0
              );

            this.workshopCount =
              Number(
                response.workshops || 0
              );

            this.programCount =
              Number(
                response.programs || 0
              );

            this.newCoursesThisMonth =
              Number(
                response.newCoursesThisMonth || 0
              );
          }
        } catch (error) {
          console.error(
            "Load Course Stats Error:",
            error
          );
        }
      },


      // ========================================
      // ADD / EDIT FORM
      // ========================================

      openForm(course = null) {
        this.formErrors = {};

        if (course) {
          /*
           * Open edit form with existing
           * list data first.
           */

          this.form = {
            ...course,
          };

          /*
           * Make sure all API fields exist.
           */

          this.form.meta_title =
            this.form.meta_title || "";

          this.form.meta_keywords =
            this.form.meta_keywords || "";

          this.form.category =
            this.form.category || "";

          this.form.creator_id =
            this.form.creator_id || "";

          this.form.course_mentor =
            this.form.course_mentor || "";

          this.form.price =
            this.form.price || 0;

          this.form.gst =
            this.form.gst || 0;

          this.form.discount =
            this.form.discount || 0;

          this.form.discount_type =
            this.form.discount_type || "flat";

          this.form.gtotal_price =
            this.form.gtotal_price || 0;

          this.form.course_image =
            this.form.course_image || "";

          this.form.course_video =
            this.form.course_video || "";

          this.form.course_icon =
            this.form.course_icon || "";

          this.form.course_features =
            this.form.course_features || "";

          this.form.course_incl_features =
            this.form.course_incl_features || "";

          this.form.course_duration =
            this.form.course_duration || 0;

          this.form.course_velidity =
            this.form.course_velidity || "12 Months";

          this.form.course_language =
            this.form.course_language || "English";

          this.form.course_certification =
            this.form.course_certification || "Yes";

          this.form.about =
            this.form.about || "";

          this.form.details =
            this.form.details || "";
        } else {
          this.form = {
            // Basic
            title: "",
            category: "",
            creator_id: "",
            course_typ: "course",

            // SEO
            meta_title: "",
            meta_keywords: "",

            // Pricing
            price: 0,
            gst: 18,
            discount: 0,
            discount_type: "flat",
            gtotal_price: 0,

            // Media
            course_icon: "",
            course_image: "",
            course_video: "",

            // Features
            course_features: "",
            course_incl_features: "",

            // Details
            course_duration: 0,
            course_velidity: "12 Months",
            course_language: "English",
            course_certification: "Yes",

            // Content
            about: "",
            details: "",

            // Mentor
            course_mentor: "",
          };
        }

        this.showModal = true;
      },


      // ========================================
      // CLOSE FORM
      // ========================================

      close() {
        this.showModal = false;
        this.form = {};
        this.formErrors = {};
      },


      showFeedback(message, title = "Notice", type = "error") {
        this.feedback = {
          type,
          title,
          message,
        };
        this.feedbackModal = true;
      },


      closeFeedback() {
        this.feedbackModal = false;
      },


      isValidUrl(value) {
        try {
          const url = new URL(value);
          return ["http:", "https:"].includes(url.protocol);
        } catch (error) {
          return false;
        }
      },


      validateForm() {
        this.formErrors = {};

        if (!this.form.title?.trim()) {
          this.showFeedback("Course title is required.", "Check the form");
          return false;
        }

        ["course_image", "course_video", "course_icon"].forEach((field) => {
          const value = String(this.form[field] || "").trim();

          if (value && !this.isValidUrl(value)) {
            this.formErrors[field] = "Enter a valid http:// or https:// URL.";
          }
        });

        if (Object.keys(this.formErrors).length) {
          this.showFeedback("Please correct the highlighted URL fields before saving.", "Invalid URL");
          return false;
        }

        return true;
      },


      // ========================================
      // VIEW COURSE
      // ========================================

      async viewCourse(course) {
        if (!course?.course_id) {
          return;
        }

        this.$router.push({
          name: "CourseDetailsScreen",
          params: { courseId: String(course.course_id) },
        });
      },


      // ========================================
      // EDIT FROM VIEW
      // ========================================

      editFromView() {
        const course =
          this.selectedCourse;

        this.viewModal = false;

        if (course) {
          this.openForm(course);
        }
      },

      manageCurriculum() {
        if (!this.selectedCourse?.course_id) {
          return;
        }

        this.viewModal = false;
        this.$router.push({
          path: "/modules",
          query: { course_id: String(this.selectedCourse.course_id) },
        });
      },

      manageFaqs() {
        if (!this.selectedCourse?.course_id) {
          return;
        }

        this.viewModal = false;
        this.$router.push({
          path: "/course-faqs",
          query: { course_id: String(this.selectedCourse.course_id) },
        });
      },


      // ========================================
      // SAVE COURSE
      // ========================================

      async save() {
        if (!this.validateForm()) {
          return;
        }

        this.saving = true;

        try {
          let response;
          const isUpdate = Boolean(this.form.course_id);
          const payload = {
            ...this.form,
            gtotal_price: this.calculatedFinalPrice,
          };

          if (this.form.course_id) {
            // ================================
            // UPDATE
            // ================================

            response =
              await updateCourseApi(
                payload
              );
          } else {
            // ================================
            // CREATE
            // ================================

            response =
              await createCourseApi(
                payload
              );
          }

          if (response?.status) {
            this.close();

            await this.loadCourses();
            await this.loadStats();

            this.showFeedback(
              response.success ||
              (isUpdate ? "Course updated successfully." : "Course created successfully."),
              isUpdate ? "Course Updated" : "Course Created",
              "success"
            );
          } else {
            this.showFeedback(
              response?.success ||
              "Course save failed."
            );
          }
        } catch (error) {
          console.error(
            "Save Course Error:",
            error
          );

          this.showFeedback(
            "Unable to save course."
          );
        } finally {
          this.saving = false;
        }
      },


      // ========================================
      // DELETE MODAL
      // ========================================

      openDelete(course) {
        if (!course?.course_id) {
          return;
        }

        this.selectedCourse =
          course;

        this.deleteModal = true;
      },


      // ========================================
      // CONFIRM DELETE
      // ========================================

      async confirmDelete() {
        if (
          !this.selectedCourse?.course_id
        ) {
          return;
        }

        this.deleting = true;

        try {
          const response =
            await deleteCourseApi(
              this.selectedCourse.course_id
            );

          if (response?.status) {
            this.deleteModal = false;
            this.selectedCourse = null;

            /*
             * If last item on current page
             * was deleted, move back one page.
             */

            if (
              this.courses.length === 1 &&
              this.page > 0
            ) {
              this.page--;
            }

            await this.loadCourses();
            await this.loadStats();

            this.showFeedback(
              response.success ||
              "Course deleted successfully.",
              "Course Deleted",
              "success"
            );
          } else {
            this.showFeedback(
              response?.success ||
              "Course deletion failed."
            );
          }
        } catch (error) {
          console.error(
            "Delete Course Error:",
            error
          );

          this.showFeedback(
            "Unable to delete course."
          );
        } finally {
          this.deleting = false;
        }
      },


      // ========================================
      // PREVIOUS PAGE
      // ========================================

      prevPage() {
        if (this.page > 0) {
          this.page--;

          this.loadCourses();
        }
      },


      // ========================================
      // NEXT PAGE
      // ========================================

      nextPage() {
        if (
          this.page <
          this.totalPages - 1
        ) {
          this.page++;

          this.loadCourses();
        }
      },
    },
  };
</script>

<style scoped>
  /* =========================================================
   PAGE
========================================================= */

  .page {
    width: 100%;
    padding: 20px;
    box-sizing: border-box;
  }


  /* =========================================================
   HEADER
========================================================= */

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 18px;
  }

  .page-header h2 {
    margin: 0;
    color: #0F172A;
    font-size: 24px;
    font-weight: 700;
  }

  .page-header p {
    margin: 5px 0 0;
    color: #64748B;
    font-size: 12px;
  }


  /* =========================================================
   BUTTONS
========================================================= */

  .btn-primary {
    height: 40px;
    padding: 0 15px;
    border: none;
    border-radius: 9px;
    background: #2563EB;
    color: white;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .btn-primary:hover {
    background: #1D4ED8;
  }

  .btn-secondary {
    height: 40px;
    padding: 0 15px;
    border: 1px solid #E2E8F0;
    border-radius: 9px;
    background: white;
    color: #334155;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
  }

  .btn-danger {
    height: 40px;
    padding: 0 15px;
    border: none;
    border-radius: 9px;
    background: #EF4444;
    color: white;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
  }


  /* =========================================================
   TOOLBAR
========================================================= */

  .toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 15px;
  }

  .toolbar input,
  .filter-select {
    height: 40px;
    padding: 0 12px;
    border: 1px solid #E2E8F0;
    border-radius: 9px;
    background: white;
    color: #334155;
    font-size: 12px;
    outline: none;
  }

  .toolbar input {
    width: 280px;
  }

  .filter-select {
    min-width: 140px;
  }

  .toolbar input:focus,
  .filter-select:focus {
    border-color: #2563EB;
  }


  /* =========================================================
   CARD
========================================================= */

  .card {
    background: white;
    border: 1px solid #E2E8F0;
    border-radius: 15px;
    overflow: hidden;
  }

  .table-wrapper {
    width: 100%;
    overflow-x: auto;
  }


  /* =========================================================
   TABLE
========================================================= */

  .modern-table {
    width: 100%;
    min-width: 1050px;
    border-collapse: collapse;
  }

  .modern-table th {
    padding: 13px 14px;
    background: #F8FAFC;
    border-bottom: 1px solid #E2E8F0;
    color: #64748B;
    text-align: left;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .3px;
    white-space: nowrap;
  }

  .modern-table td {
    padding: 13px 14px;
    border-bottom: 1px solid #F1F5F9;
    color: #334155;
    font-size: 12px;
    white-space: nowrap;
  }

  .modern-table tbody tr:hover {
    background: #FAFCFF;
  }


  /* =========================================================
   COURSE
========================================================= */

  .course-info {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 250px;
  }

  .course-thumb {
    width: 58px;
    height: 40px;
    min-width: 58px;
    object-fit: cover;
    border-radius: 7px;
    background: #F1F5F9;
  }

  .course-thumb.placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94A3B8;
    font-size: 18px;
  }

  .course-name strong {
    display: block;
    color: #0F172A;
    font-size: 12px;
    font-weight: 700;
    max-width: 210px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .course-name small {
    display: block;
    margin-top: 3px;
    color: #94A3B8;
    font-size: 9px;
  }


  /* =========================================================
   BADGES
========================================================= */

  .category-badge,
  .type-badge {
    display: inline-flex;
    align-items: center;
    padding: 5px 9px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 600;
  }

  .category-badge {
    background: #EFF6FF;
    color: #2563EB;
  }

  .type-badge {
    background: #F5F3FF;
    color: #7C3AED;
  }


  /* =========================================================
   PRICE
========================================================= */

  .price-info strong {
    display: block;
    color: #0F172A;
    font-size: 12px;
  }

  .discount {
    display: inline-block;
    margin-top: 3px;
    color: #059669;
    font-size: 9px;
  }


  /* =========================================================
   ACTIONS
========================================================= */

  .icon-btn {
    width: 31px;
    height: 31px;
    border: none;
    border-radius: 7px;
    margin-left: 5px;
    cursor: pointer;
    font-size: 16px;
  }

  .icon-btn.view {
    background: #F1F5F9;
    color: #475569;
  }

  .icon-btn.edit {
    background: #EFF6FF;
    color: #2563EB;
  }

  .icon-btn.delete {
    background: #FEF2F2;
    color: #EF4444;
  }

  .icon-btn:hover {
    transform: translateY(-1px);
  }


  /* =========================================================
   EMPTY
========================================================= */

  .empty-state {
    padding: 50px !important;
    text-align: center !important;
  }

  .empty-state i {
    color: #94A3B8;
    font-size: 35px;
  }

  .empty-state h4 {
    margin: 8px 0 3px;
    color: #334155;
  }

  .empty-state p {
    margin: 0;
    color: #94A3B8;
    font-size: 11px;
  }


  /* =========================================================
   PAGINATION
========================================================= */

  .pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
  }

  .pagination button {
    height: 34px;
    padding: 0 12px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    background: white;
    color: #334155;
    font-size: 11px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .pagination button:disabled {
    opacity: .45;
    cursor: not-allowed;
  }

  .pagination span {
    color: #64748B;
    font-size: 11px;
  }


  /* =========================================================
   MODAL
========================================================= */

  .modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(15, 23, 42, .5);
    backdrop-filter: blur(4px);
  }

  .modal-box {
    width: 100%;
    max-width: 430px;
    max-height: 90vh;
    overflow-y: auto;
    padding: 22px;
    border-radius: 16px;
    background: white;
    box-shadow: 0 25px 60px rgba(15, 23, 42, .18);
  }

  .large-modal {
    max-width: 760px;
  }

  .view-modal {
    max-width: 650px;
  }

  .modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 15px;
    margin-bottom: 20px;
  }

  .modal-header h3 {
    margin: 0;
    color: #0F172A;
    font-size: 18px;
    font-weight: 700;
  }

  .modal-header p {
    margin: 4px 0 0;
    color: #94A3B8;
    font-size: 11px;
  }

  .modal-close {
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: #F8FAFC;
    color: #64748B;
    cursor: pointer;
    font-size: 18px;
  }


  /* =========================================================
   FORM
========================================================= */

  .form-section {
    padding: 18px 0;
    border-top: 1px solid #F1F5F9;
  }

  .form-section:first-of-type {
    border-top: none;
  }

  .form-section h4 {
    margin: 0 0 14px;
    color: #334155;
    font-size: 13px;
    font-weight: 700;
  }

  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 13px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .form-group.full {
    grid-column: 1 / -1;
  }

  .form-group label {
    color: #475569;
    font-size: 10px;
    font-weight: 700;
  }

  .form-group input,
  .form-group select,
  .form-group textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 10px 11px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    background: white;
    color: #334155;
    font-size: 11px;
    outline: none;
    font-family: inherit;
  }

  .form-group textarea {
    min-height: 85px;
    resize: vertical;
  }

  .form-group input:focus,
  .form-group select:focus,
  .form-group textarea:focus {
    border-color: #2563EB;
  }

  .field-error {
    color: #DC2626;
    font-size: 10px;
    line-height: 1.3;
  }

  .field-hint {
    color: #64748B;
    font-size: 10px;
    line-height: 1.3;
  }


  /* =========================================================
   MODAL ACTIONS
========================================================= */

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 9px;
    padding-top: 18px;
    margin-top: 5px;
    border-top: 1px solid #F1F5F9;
  }


  /* =========================================================
   VIEW
========================================================= */

  .course-view {
    display: flex;
    gap: 18px;
    margin-bottom: 20px;
  }

  .view-image {
    width: 190px;
    height: 120px;
    object-fit: cover;
    border-radius: 10px;
  }

  .view-content {
    flex: 1;
  }

  .view-content h2 {
    margin: 0 0 8px;
    color: #0F172A;
    font-size: 20px;
  }

  .view-about {
    margin: 0 0 15px;
    color: #64748B;
    font-size: 11px;
    line-height: 1.6;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .detail-grid div {
    padding: 10px;
    border-radius: 8px;
    background: #F8FAFC;
  }

  .detail-grid span {
    display: block;
    color: #94A3B8;
    font-size: 9px;
  }

  .detail-grid strong {
    display: block;
    margin-top: 3px;
    color: #334155;
    font-size: 11px;
  }


  /* =========================================================
   DELETE
========================================================= */

  .delete-box {
    text-align: center;
    max-width: 360px;
  }

  .delete-icon {
    width: 55px;
    height: 55px;
    margin: 0 auto 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    background: #FEF2F2;
    color: #EF4444;
    font-size: 25px;
  }

  .delete-box h3 {
    margin: 0;
    color: #0F172A;
    font-size: 17px;
  }

  .delete-box p {
    margin: 7px 0 0;
    color: #64748B;
    font-size: 11px;
    line-height: 1.5;
  }

  .delete-box .modal-actions {
    justify-content: center;
  }

  .feedback-box {
    max-width: 380px;
    text-align: center;
  }

  .feedback-icon {
    width: 52px;
    height: 52px;
    margin: 0 auto 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    font-size: 26px;
  }

  .feedback-icon.feedback-success {
    background: #ECFDF5;
    color: #059669;
  }

  .feedback-icon.feedback-error {
    background: #FEF2F2;
    color: #DC2626;
  }

  .feedback-box h3 {
    margin: 0;
    color: #0F172A;
    font-size: 17px;
  }

  .feedback-box p {
    margin: 8px 0 0;
    color: #64748B;
    font-size: 12px;
    line-height: 1.5;
  }

  .feedback-box .modal-actions {
    justify-content: center;
  }


  /* =========================================================
   RESPONSIVE
========================================================= */

  @media (max-width: 768px) {

    .page {
      padding: 15px;
    }

    .page-header {
      align-items: flex-start;
      flex-direction: column;
    }

    .btn-primary {
      width: 100%;
      justify-content: center;
    }

    .toolbar {
      flex-direction: column;
      align-items: stretch;
    }

    .toolbar input,
    .filter-select {
      width: 100%;
    }

    .form-grid {
      grid-template-columns: 1fr;
    }

    .form-group.full {
      grid-column: auto;
    }

    .course-view {
      flex-direction: column;
    }

    .view-image {
      width: 100%;
      height: 180px;
    }

  }


  @media (max-width: 480px) {

    .page {
      padding: 12px;
    }

    .page-header h2 {
      font-size: 21px;
    }

    .modal-overlay {
      padding: 10px;
    }

    .modal-box {
      padding: 17px;
    }

    .detail-grid {
      grid-template-columns: 1fr;
    }

  }
</style>